import { useId, useMemo } from 'react'
import {
  useGetAuthzMeQuery,
  useListProblemOccurrencesQuery,
  useListOpenProblemTasksQuery,
  useWorkScope,
  useListCustomAgentTasksQuery
} from '@axilon-apps/domains-ontology-ui-state'

import { TIER_PILL_CLASSES } from '../primitives/severityTier'
import { useDebugMode } from '../../lib/debug-mode'
import { useChatHandoff } from '../../lib/use-chat-handoff'
import { cn } from '../../lib/utils'
import { workPagesForAccess } from '../../lib/work-page'
import { topOccurrenceWork, type OpenWorkItem } from './open-work'
import { CHAT_LANDING_MEASURE } from './chat-measure'

/** The top open tasks/problems for the current user's work page. */
export function useOpenWorkState(options?: { skip?: boolean }) {
  const skip = options?.skip === true
  const debug = useDebugMode()
  const { data: authz } = useGetAuthzMeQuery(undefined, { skip })
  const roles = authz?.roles
  const permissions = authz?.permissions
  const pages = useMemo(
    () => workPagesForAccess(roles ?? [], permissions ?? [], debug),
    [roles, permissions, debug]
  )
  const wantsTasks = !skip && authz !== undefined && pages.includes('tasks')
  const wantsProblems = !skip && authz !== undefined && pages.includes('problems')
  const scope = useWorkScope()
  const tasks = useListOpenProblemTasksQuery(scope, { skip: !wantsTasks })
  const wantsLegacy = wantsTasks && permissions?.includes('agents:custom:config/read') === true
  const legacy = useListCustomAgentTasksQuery(scope, { skip: !wantsLegacy })
  const problems = useListProblemOccurrencesQuery(scope, { skip: !wantsProblems })
  const items = useMemo(
    () =>
      skip
        ? []
        : topOccurrenceWork(
            wantsTasks && !tasks.isError ? (tasks.currentData ?? []) : [],
            wantsProblems && !problems.isError ? (problems.currentData ?? []) : [],
            wantsLegacy && !legacy.isError ? (legacy.currentData ?? []) : [],
            pages
          ),
    [
      skip,
      tasks.currentData,
      tasks.isError,
      problems.currentData,
      problems.isError,
      legacy.currentData,
      legacy.isError,
      wantsTasks,
      wantsProblems,
      wantsLegacy,
      pages
    ]
  )
  const isError =
    (wantsTasks && tasks.isError) ||
    (wantsProblems && problems.isError) ||
    (wantsLegacy && legacy.isError)
  const isLoading =
    (wantsTasks && tasks.isLoading) ||
    (wantsProblems && problems.isLoading) ||
    (wantsLegacy && legacy.isLoading)
  return {
    items,
    isError,
    isLoading,
    retry: () => {
      if (wantsTasks) void tasks.refetch()
      if (wantsProblems) void problems.refetch()
      if (wantsLegacy) void legacy.refetch()
    }
  }
}
export function useOpenWorkItems(options?: { skip?: boolean }): OpenWorkItem[] {
  return useOpenWorkState(options).items
}

/** Chat home: open work grouped by kind; clicking a row asks about it in chat. */
export function OpenWorkList({
  items,
  layout,
  error = false,
  loading = false,
  retry
}: {
  items: OpenWorkItem[]
  layout: 'rail' | 'page'
  error?: boolean
  loading?: boolean
  retry?: () => void
}) {
  const { askInChat } = useChatHandoff()
  const headingId = useId()
  const groups = [
    { kind: 'problem', label: 'Problems', items: items.filter((item) => item.kind === 'problem') },
    { kind: 'task', label: 'Tasks', items: items.filter((item) => item.kind === 'task') }
  ]

  return (
    <div
      className={cn(
        'flex flex-col',
        layout === 'page' ? cn(CHAT_LANDING_MEASURE, 'gap-4') : 'gap-3 px-6 pb-3'
      )}
    >
      {error && (
        <p role="alert" className="m-0 text-12 text-st-down">
          Couldn't load some work.{' '}
          <button
            type="button"
            onClick={retry}
            className="rounded text-brand underline focus-visible:ring-2 focus-visible:ring-brand"
          >
            Retry
          </button>
        </p>
      )}
      {loading && (
        <p role="status" className="m-0 text-12 text-ink-3">
          Loading work…
        </p>
      )}
      {groups.map((group) =>
        group.items.length > 0 ? (
          <section key={group.kind} aria-labelledby={`${headingId}-${group.kind}`}>
            <h2
              id={`${headingId}-${group.kind}`}
              className="m-0 mb-2 text-12 font-medium text-muted-foreground"
            >
              {group.label}
            </h2>
            <ul
              aria-labelledby={`${headingId}-${group.kind}`}
              className="m-0 flex list-none flex-col gap-1.5 p-0"
            >
              {group.items.map((item) => (
                <li key={item.id}>
                  <button
                    type="button"
                    onClick={() => askInChat({ title: item.title, context: item.context })}
                    className={cn(
                      'flex w-full items-center gap-2 rounded-lg border border-border bg-card text-left transition-colors hover:border-primary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                      layout === 'page' ? 'px-3.5 py-2.5 text-14' : 'px-2.5 py-1.5 text-12'
                    )}
                  >
                    <span className="min-w-0 flex-1 truncate font-medium text-foreground">
                      {item.title}
                    </span>
                    {item.kind === 'task' && item.pill !== 'task' ? (
                      <span
                        className={cn(
                          'shrink-0 rounded-full px-2 py-0.5 text-10 font-medium capitalize',
                          TIER_PILL_CLASSES[item.tier]
                        )}
                      >
                        {item.pill}
                      </span>
                    ) : null}
                  </button>
                </li>
              ))}
            </ul>
          </section>
        ) : null
      )}
    </div>
  )
}
